import React from 'react';
import { AnimatedSection } from '../components/ui/AnimatedSection';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { ShieldCheckIcon, TargetIcon, ZapIcon, HeartIcon } from 'lucide-react';
export function About() {
  return <main className="w-full pt-24" data-id="element-121">
      {/* Hero */}
      <section className="py-20 md:py-32 bg-dark relative border-b border-border" data-id="element-122">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-123">
          <AnimatedSection data-id="element-124">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 font-heading" data-id="element-125">
              About BioShift
            </h1>
            <p className="text-xl text-muted leading-relaxed" data-id="element-126">
              We are on a mission to help people become physically strong,
              metabolically healthy, mentally disciplined, and spiritually
              aligned.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-24 md:py-32 bg-surface" data-id="element-127">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-128">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-129">
            <AnimatedSection direction="right" data-id="element-130">
              <div className="sticky top-32" data-id="element-131">
                <img src="https://cdn.magicpatterns.com/uploads/fGbeAn9xwt3ndk3U69NmMv/copy_34613203-5366-4535-84D5-37C3D87017DE.jpg" alt="Elisa Chiaradia" className="w-full aspect-[3/4] object-cover" data-id="element-132" />
                <div className="mt-6" data-id="element-133">
                  <h3 className="text-2xl font-bold text-white font-heading" data-id="element-134">
                    Elisa Chiaradia
                  </h3>
                  <p className="text-gold" data-id="element-135">Founder & Head Coach</p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection direction="left" className="prose prose-invert prose-lg max-w-none" data-id="element-136">
              <SectionHeading overline="The Origin" title="Born From Purpose" align="left" data-id="element-137" />
              <div className="text-muted space-y-6 mt-8" data-id="element-138">
                <p data-id="element-139">
                  My journey into health and performance wasn't born out of a
                  desire to look a certain way. It was born out of necessity and
                  personal tragedy.
                </p>
                <p data-id="element-140">
                  After watching multiple family members suffer from cancer,
                  including the devastating loss of my father, I was driven to
                  understand the true root causes of chronic disease and
                  metabolic dysfunction. The conventional answers weren't
                  enough. I needed to know *why* our bodies break down and, more
                  importantly, how we can build them to be resilient.
                </p>
                <p data-id="element-141">
                  For years, I immersed myself in the study of nutrition,
                  training, and health science. I tried every protocol, read the
                  research, and worked with countless individuals. Through this
                  relentless pursuit, a clear pattern emerged.
                </p>
                <h4 className="text-white text-2xl font-heading mt-10 mb-4" data-id="element-142">
                  The Missing Link
                </h4>
                <p data-id="element-143">
                  I discovered that true health transformation cannot happen in
                  silos. You can't just fix the diet and ignore the muscle. You
                  can't build the body and ignore the mind. And for lasting,
                  profound change, you cannot ignore the spirit.
                </p>
                <p data-id="element-144">
                  BioShift was created to integrate these elements: strength
                  training to build metabolic armor, functional nutrition to
                  restore internal balance, disciplined mindset to sustain the
                  work, and faith-based identity to anchor the purpose.
                </p>
                <p data-id="element-145">
                  We don't just help people lose weight. We help them reclaim
                  control of their lives so they can fulfill their highest
                  calling with energy, strength, and clarity.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 md:py-32 bg-dark" data-id="element-146">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-147">
          <AnimatedSection data-id="element-148">
            <SectionHeading overline="Our Foundation" title="Core Values" data-id="element-149" />
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16" data-id="element-150">
            {[{
            icon: ShieldCheckIcon,
            title: 'Truth in Science',
            desc: 'We rely on data, blood work, and proven physiological principles, not fads or trends.'
          }, {
            icon: TargetIcon,
            title: 'Relentless Discipline',
            desc: 'Motivation fades. Discipline builds the structure required for lifelong transformation.'
          }, {
            icon: ZapIcon,
            title: 'Root Cause Resolution',
            desc: 'We refuse to put band-aids on symptoms. We dig deep to fix the underlying metabolic issues.'
          }, {
            icon: HeartIcon,
            title: 'Faith-Aligned Purpose',
            desc: "We believe the body is a temple, and optimizing health is a stewardship of the life we've been given."
          }].map((value, i) => <AnimatedSection key={i} delay={i * 0.1} data-id="element-151">
                <div className="bg-surface p-10 border border-border hover:border-gold/50 transition-colors h-full" data-id="element-152">
                  <value.icon className="w-10 h-10 text-gold mb-6" data-id="element-153" />
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-154">
                    {value.title}
                  </h3>
                  <p className="text-muted" data-id="element-155">{value.desc}</p>
                </div>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-surface border-t border-border text-center" data-id="element-156">
        <AnimatedSection data-id="element-157">
          <h2 className="text-4xl font-bold text-white mb-8 font-heading" data-id="element-158">
            Ready to start your journey?
          </h2>
          <Button href="https://holistic-health.bioshift.net/calender-booking" size="lg" data-id="element-159">
            Apply for Coaching
          </Button>
        </AnimatedSection>
      </section>
    </main>;
}